// `grid vault set <key>` — #1766. Upsert a vault item in a grid: create it
// (POST) when the key is absent in the scope, else update its value (PATCH).
//
// The value is write-only: read from `--value-stdin` or a hidden prompt, never
// from argv, and never echoed back. `set` prints only the key and the grid it
// targeted.

import { Command } from 'commander';
import { getV2Client, type IV2Client } from '../../api/v2-client.js';
import { handleV2Error } from '../../handle-error.js';
import { addGridOption, gridSlug, type GridOpts } from '../../grid-opt.js';
import {
  announceGridTarget,
  findVaultItemByKey,
  readSecretValue,
  resolveVaultGrid,
  vaultItemPath,
  vaultItemsPath,
} from './shared.js';

export interface RunVaultSetOpts {
  client: IV2Client;
  key: string;
  gridOverride: string | undefined;
  /** Item type for a newly-created item. Defaults to `secret`. Immutable on update. */
  type?: string;
  /** Scope for a newly-created item and the existence lookup. Defaults to `org`. */
  scope?: string;
  /** Optional demo value (non-secret placeholder shown in Console). */
  demo?: string;
  /** Optional human description. */
  description?: string;
  /** Read the value from stdin instead of prompting. */
  valueStdin?: boolean;
  /** Test seam: injected hidden-prompt reader. */
  readValue?: () => Promise<string>;
  /** Test seam: injected stdin reader. */
  readStdin?: () => Promise<string>;
}

export async function runVaultSet(opts: RunVaultSetOpts): Promise<void> {
  const type = opts.type ?? 'secret';
  const scope = opts.scope ?? 'org';

  const grid = await resolveVaultGrid(opts.client, opts.gridOverride);
  announceGridTarget(grid);

  const value = await readSecretValue({
    valueStdin: opts.valueStdin,
    readValue: opts.readValue,
    readStdin: opts.readStdin,
  });

  const existing = await findVaultItemByKey(opts.client, grid.gridId, opts.key, scope);

  if (existing) {
    const body: Record<string, unknown> = { value };
    if (opts.demo !== undefined) body.demo_value = opts.demo;
    if (opts.description !== undefined) body.description = opts.description;
    await opts.client.patch(vaultItemPath(grid.gridId, existing.item_id), body);
    process.stdout.write(`Updated ${opts.key} in ${grid.slug}.\n`);
    return;
  }

  const body: Record<string, unknown> = { key: opts.key, type, scope, value };
  if (opts.demo !== undefined) body.demo_value = opts.demo;
  if (opts.description !== undefined) body.description = opts.description;
  await opts.client.post(vaultItemsPath(grid.gridId), body);
  process.stdout.write(`Created ${opts.key} in ${grid.slug}.\n`);
}

export const vaultSetCommand: Command = addGridOption(
  new Command('set')
    .description('Create or update a vault item (the value is read out-of-band, never from argv)')
    .argument('<key>', 'Item key (e.g. STRIPE_KEY)')
    .option('--type <type>', 'Item type: secret | api_key | url | json | google_service_key | file', 'secret')
    .option('--scope <scope>', 'Scope: org | personal', 'org')
    .option('--demo <value>', 'Non-secret demo/placeholder value')
    .option('--description <text>', 'Human description')
    .option('--value-stdin', 'Read the value from stdin instead of prompting'),
  'Grid to write to (defaults to the active grid)',
).action(async (
  key: string,
  opts: GridOpts & {
    type?: string; scope?: string; demo?: string; description?: string; valueStdin?: boolean;
  },
) => {
  try {
    const gridOverride = gridSlug(opts);
    const client = await getV2Client(gridOverride ?? undefined);
    await runVaultSet({
      client,
      key,
      gridOverride,
      type: opts.type,
      scope: opts.scope,
      demo: opts.demo,
      description: opts.description,
      valueStdin: opts.valueStdin,
    });
  } catch (err) {
    await handleV2Error(err);
  }
});
